import { Alert, Box, Button, Center, Skeleton, Stack, Tabs, Text } from "@mantine/core";
import { Component, lazy, type ReactNode, Suspense, useState } from "react";

import { ClientOnly } from "@mountayaapp/markdoc/renderers";

import { ELEMENT_DEMOS, type ElementDemo, type ElementDemoName, isDemoName } from "../rules/demos";

// The element packages and their stylesheets live behind this one dynamic import, so
// they land in a chunk of their own and a page with no demo never pays for them.
const Client = lazy(() => import("./Client").then((m) => ({ default: m.ElementClient })));

// And the map renderer lives behind a SECOND one, reached only from the poster below. Two
// dynamic imports rather than one because they cost differently: the elements are worth loading
// for a reader who scrolled to them, and a WebGL renderer is not.
const MapClient = lazy(() => import("./MapClient").then((m) => ({ default: m.MapClient })));

// And the source panel behind a THIRD, so a reader who never opens the Code tab downloads
// neither the highlighter nor any demo's source.
const CodeTab = lazy(() => import("./CodeTab").then((m) => ({ default: m.CodeTab })));

/**
 * ElementProps configure the {% element %} tag. `name` selects a registered demo, whose
 * entry supplies the height the frame reserves and the label it is announced by.
 */
interface ElementProps {
  name?: string;
}

/**
 * ElementBoundary catches a demo that throws, including a chunk that fails to load.
 *
 * It sits OUTSIDE the Suspense boundary on purpose. `dist/assets` is served immutable
 * and synced with `--delete-removed`, so a reader holding a tab open across a deploy
 * asks for a hash that no longer exists. A boundary inside Suspense would not see that
 * rejection, and the demo would sit in a skeleton forever rather than saying anything.
 */
class ElementBoundary extends Component<
  { children: ReactNode; label: string },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (this.state.failed) {
      return (
        <Alert color="red" my="md" title="This example could not load">
          The live {this.props.label} example is unavailable. The code beside it is unaffected — it
          is checked against the package on every build.
        </Alert>
      );
    }

    return this.props.children;
  }
}

/**
 * MapPoster stands in for a map demo until a reader asks for one.
 *
 * The renderer is ~270 KB gzipped and no other page on this site needs a byte of it, so a
 * reader scrolling past this must not download one. A button rather than an intersection
 * observer, and that is the honest version: the map draws live tiles against a real key, so
 * "came into view" is not consent to spend a request.
 *
 * It reserves the demo's own height, so pressing it moves nothing on the page.
 */
const MapPoster = ({ demo, name }: { demo: ElementDemo; name: ElementDemoName }) => {
  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <Suspense fallback={<Skeleton h={demo.height} radius="md" />}>
        <MapClient name={name} />
      </Suspense>
    );
  }

  return (
    <Center h={demo.height}>
      <Stack align="center" gap="xs">
        <Text c="dimmed" size="sm" ta="center">
          This example draws a live map against the demo key.
        </Text>
        <Button onClick={() => setLoaded(true)} variant="default">
          Load the live map
        </Button>
      </Stack>
    </Center>
  );
};

/**
 * Element is the {% element %} renderer the documentation app injects: one real element
 * from the published package, mounted against a baked route, beside the source that drew it.
 *
 * The two fallbacks differ, and the difference is load-bearing. The client-only gate's
 * skeleton is what the prerender emits AND what the first client render emits, so
 * hydration matches exactly; it does not animate, because static markup that animates
 * spends a reader's CPU describing nothing. The Suspense fallback is the same box,
 * animated — by the time it shows, the browser is fetching a chunk and the motion means
 * something. Both reserve the demo's declared height, so nothing on the page moves when
 * the element arrives.
 */
export const Element = ({ name }: ElementProps) => {
  if (name === undefined || !isDemoName(name)) {
    return (
      <Alert color="red" my="md" title="Unknown example">
        No live example is registered as "{name ?? ""}".
      </Alert>
    );
  }

  const demo: ElementDemo = ELEMENT_DEMOS[name as ElementDemoName];
  const reserved = <Skeleton animate={false} h={demo.height} radius="md" />;

  return (
    // mantine:exempt — framed demo: a 1px theme border and a theme radius on one box,
    // matching the hosted embed's frame beside it; Mantine has no border-side prop.
    <Box
      aria-label={`Live ${demo.label} example`}
      my="md"
      p="md"
      role="group"
      style={{
        border: "1px solid var(--mantine-color-default-border)",
        borderRadius: "var(--mantine-radius-md)",
      }}
    >
      <Tabs defaultValue="preview" variant="outline">
        <Tabs.List mb="md">
          <Tabs.Tab value="preview">Preview</Tabs.Tab>
          <Tabs.Tab value="code">Code</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="preview">
          <ClientOnly fallback={reserved}>
            <ElementBoundary label={demo.label}>
              {demo.withMap === true ? (
                <MapPoster demo={demo} name={name as ElementDemoName} />
              ) : (
                <Suspense fallback={<Skeleton h={demo.height} radius="md" />}>
                  <Client name={name as ElementDemoName} />
                </Suspense>
              )}
            </ElementBoundary>
          </ClientOnly>
        </Tabs.Panel>
        {/*
         * `keepMounted={false}` is what makes the third dynamic import worth having. Mantine
         * mounts every panel by default and hides the inactive ones, which would fetch the
         * highlighter and this demo's source on arrival — for a panel nobody opened — and put
         * the cost back that the lazy glob exists to avoid.
         */}
        <Tabs.Panel keepMounted={false} value="code">
          <ElementBoundary label={demo.label}>
            <Suspense fallback={<Skeleton h={220} radius="md" />}>
              <CodeTab component={demo.component} />
            </Suspense>
          </ElementBoundary>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
};
